import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const VAT_STORAGE_KEY = 'sofia-vat-display-mode';
const VAT_RATE = 0.2;

/**
 * @param {string | null | undefined} value
 * @returns {number | null}
 */
function parsePriceValue(value) {
  if (!value) return null;
  const match = String(value).match(/[\d,]+\.?\d*/);
  if (!match) return null;
  return parseFloat(match[0].replace(/,/g, ''));
}

function isVatToggleEnabled() {
  const header =
    document.getElementById('header-component') ||
    document.querySelector('header-component') ||
    document.querySelector('.header') ||
    document.querySelector('header');

  const hasPageToggle = !!document.querySelector('[data-vat-toggle]');

  if (!(header instanceof Element)) return hasPageToggle;

  const enabledAttr = header.getAttribute('data-vat-toggle-enabled');
  if (enabledAttr === 'false' && !hasPageToggle) {
    return false;
  }
  return true;
}

function getEffectiveVatMode() {
  let mode = localStorage.getItem(VAT_STORAGE_KEY);
  if (mode !== 'incl' && mode !== 'excl') {
    mode = 'incl';
  }
  if (!isVatToggleEnabled()) {
    mode = 'incl';
  }
  return mode;
}

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  constructor() {
    super();
    this.originalPrice = null;
    this.originalPriceIncl = null;
    this.currentDiscount = 0;
  }

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('quantity-discount-update', this.updatePriceWithDiscount);
    
    // Store the original price on first load
    this.storeOriginalPrice();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('quantity-discount-update', this.updatePriceWithDiscount);
  }
  
  storeOriginalPrice() {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;
    
    const priceEl =
      priceContainer.querySelector('[data-vat-incl]') ||
      priceContainer.querySelector('[data-vat-excl]') ||
      priceContainer;

    const inclAttr = priceEl?.getAttribute?.('data-vat-incl');
    const exclAttr = priceEl?.getAttribute?.('data-vat-excl');

    const inclValue = parsePriceValue(inclAttr);
    const exclValue = parsePriceValue(exclAttr);
    const textValue = parsePriceValue(priceContainer.textContent.trim());

    if (inclValue !== null) {
      this.originalPriceIncl = inclValue;
      this.originalPrice = inclValue;
      return;
    }

    if (exclValue !== null) {
      this.originalPriceIncl = exclValue * (1 + VAT_RATE);
      this.originalPrice = this.originalPriceIncl;
      return;
    }

    if (textValue !== null) {
      this.originalPriceIncl = textValue;
      this.originalPrice = textValue;
    }
  }
  
  updatePriceWithDiscount = (event) => {
    const { discountPercent } = event.detail;
    this.currentDiscount = discountPercent;
    
    if (!this.originalPriceIncl) {
      this.storeOriginalPrice();
    }
    
    if (!this.originalPriceIncl || discountPercent === 0) {
      // No discount, restore original price
      this.restoreOriginalPrice();
      return;
    }
    
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;
    
    // Calculate discounted price
    const discountedPrice = this.originalPriceIncl * (1 - discountPercent / 100);
    
    // Get currency symbol and format from original
    const originalText = priceContainer.textContent.trim();
    const currencyMatch = originalText.match(/[£$€¥]/);
    const currency = currencyMatch ? currencyMatch[0] : '£';
    const hasCurrencyCode = originalText.includes('GBP') || originalText.includes('USD') || originalText.includes('EUR');
    const currencyCode = hasCurrencyCode ? originalText.match(/[A-Z]{3}/)?.[0] || '' : '';
    
    // Treat originalPrice as INCL. VAT, compute excl. VAT for display.
    const originalExclVat = this.originalPriceIncl / (1 + VAT_RATE);
    const discountedExclVat = discountedPrice / (1 + VAT_RATE);

    const formattedOriginalInclVat = `${currency}${this.originalPriceIncl.toFixed(2)}`;
    const formattedDiscountedInclVat = `${currency}${discountedPrice.toFixed(2)}`;
    const formattedOriginal = `${currency}${originalExclVat.toFixed(2)}`;
    const formattedDiscounted = `${currency}${discountedExclVat.toFixed(2)}`;
    
    // Get current VAT mode from localStorage
    const currentVatMode = getEffectiveVatMode();
    const showInclVat = currentVatMode === 'incl';
    
    // Determine which price to display based on current VAT mode
    const saleDisplayPrice = showInclVat ? formattedDiscountedInclVat : formattedDiscounted;
    const regDisplayPrice = showInclVat ? formattedOriginalInclVat : formattedOriginal;
    
    // Update the price display with discounted price first, then strikethrough original
    priceContainer.innerHTML = `
      <span class="price-item price-item--sale" style="margin-right: 8px; display: inline-block; will-change: transform, opacity;">
        ${saleDisplayPrice}${currencyCode ? ' ' + currencyCode : ''}
      </span>
      <span class="price-item price-item--regular" style="text-decoration: line-through; opacity: 0.4; display: inline-block; will-change: transform, opacity;">
        ${regDisplayPrice}${currencyCode ? ' ' + currencyCode : ''}
      </span>
    `;
    
    const saleEl = priceContainer.querySelector('.price-item--sale');
    const regEl = priceContainer.querySelector('.price-item--regular');

    if (saleEl) {
      saleEl.setAttribute(
        'data-vat-excl',
        `${formattedDiscounted}${currencyCode ? ' ' + currencyCode : ''}`
      );
      saleEl.setAttribute(
        'data-vat-incl',
        `${formattedDiscountedInclVat}${currencyCode ? ' ' + currencyCode : ''}`
      );
    }

    if (regEl) {
      regEl.setAttribute(
        'data-vat-excl',
        `${formattedOriginal}${currencyCode ? ' ' + currencyCode : ''}`
      );
      regEl.setAttribute(
        'data-vat-incl',
        `${formattedOriginalInclVat}${currencyCode ? ' ' + currencyCode : ''}`
      );
    }
    
    // Initial states: sale hidden on the right, regular in place
    saleEl.style.opacity = '0';
    saleEl.style.transform = 'translateX(20px)';
    regEl.style.opacity = '0.4';
    regEl.style.transform = 'translateX(0)';
    
    // Apply transition
    saleEl.style.transition = 'transform 250ms ease, opacity 250ms ease';
    regEl.style.transition = 'transform 250ms ease, opacity 250ms ease';
    
    // Next frame: bring sale in from right, push regular slightly left
    requestAnimationFrame(() => {
      saleEl.style.opacity = '1';
      saleEl.style.transform = 'translateX(0)';
      regEl.style.transform = 'translateX(0)';
      
      // Re-apply VAT mode to ensure consistency
      this.reapplyVatMode();
    });
  };
  
  /**
   * Re-apply the current VAT mode to price elements
   */
  reapplyVatMode() {
    const currentVatMode = getEffectiveVatMode();
    
    // Use the global applyVatMode function if available, or apply directly
    if (typeof applyVatMode === 'function') {
      applyVatMode(currentVatMode);
    } else {
      // Fallback: apply directly to elements in this component
      const els = this.querySelectorAll('[data-vat-excl]');
      els.forEach((el) => {
        const excl = el.getAttribute('data-vat-excl');
        const incl = el.getAttribute('data-vat-incl');
        const fromLabel = el.getAttribute('data-vat-from-label');
        
        let priceToDisplay = '';
        if (currentVatMode === 'incl' && incl) {
          priceToDisplay = incl;
        } else if (excl) {
          priceToDisplay = excl;
        }
        
        // If there's a "from label" prefix (e.g., "Starting from"), preserve it
        if (fromLabel && priceToDisplay) {
          el.textContent = fromLabel + ' ' + priceToDisplay;
        } else if (priceToDisplay) {
          el.textContent = priceToDisplay;
        }
      });
    }
  }
  
  restoreOriginalPrice() {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer || !this.originalPriceIncl) return;
    
    const existingSale = priceContainer.querySelector('.price-item--sale');
    const existingReg = priceContainer.querySelector('.price-item--regular');
    
    if (existingSale && existingReg) {
      // Animate sale element out to the left
      existingSale.style.transition = 'transform 250ms ease, opacity 250ms ease';
      existingReg.style.transition = 'transform 250ms ease, opacity 250ms ease';
      
      existingSale.style.opacity = '0';
      existingSale.style.transform = 'translateX(-20px)';
      existingReg.style.transform = 'translateX(0)';
      
      // After animation, replace with just the original price
      setTimeout(() => {
        const originalText = priceContainer.textContent.trim();
        const currencyMatch = originalText.match(/[£$€¥]/);
        const currency = currencyMatch ? currencyMatch[0] : '£';
        const hasCurrencyCode = originalText.includes('GBP') || originalText.includes('USD') || originalText.includes('EUR');
        const currencyCode = hasCurrencyCode ? originalText.match(/[A-Z]{3}/)?.[0] || '' : '';
        const originalExclVat = this.originalPriceIncl / (1 + VAT_RATE);
        const formattedOriginal = `${currency}${originalExclVat.toFixed(2)}`;
        const formattedOriginalInclVat = `${currency}${this.originalPriceIncl.toFixed(2)}`;
        
        // Get current VAT mode from localStorage
        const currentVatMode = getEffectiveVatMode();
        const showInclVat = currentVatMode === 'incl';
        const displayPrice = showInclVat ? formattedOriginalInclVat : formattedOriginal;
        
        priceContainer.innerHTML = `
          <span
            class="price-item price-item--regular"
            data-vat-excl="${formattedOriginal}${currencyCode ? ' ' + currencyCode : ''}"
            data-vat-incl="${formattedOriginalInclVat}${currencyCode ? ' ' + currencyCode : ''}"
          >
            ${displayPrice}${currencyCode ? ' ' + currencyCode : ''}
          </span>
        `;
        
        // Re-apply VAT mode to ensure consistency
        this.reapplyVatMode();
      }, 250);
      return;
    }
    
    // Fallback: directly render original price
    const originalText = priceContainer.textContent.trim();
    const currencyMatch = originalText.match(/[£$€¥]/);
    const currency = currencyMatch ? currencyMatch[0] : '£';
    const hasCurrencyCode = originalText.includes('GBP') || originalText.includes('USD') || originalText.includes('EUR');
    const currencyCode = hasCurrencyCode ? originalText.match(/[A-Z]{3}/)?.[0] || '' : '';
    const originalExclVat = this.originalPriceIncl / (1 + VAT_RATE);
    const formattedOriginal = `${currency}${originalExclVat.toFixed(2)}`;
    const formattedOriginalInclVat = `${currency}${this.originalPriceIncl.toFixed(2)}`;
    
    // Get current VAT mode from localStorage
    const currentVatMode = getEffectiveVatMode();
    const showInclVat = currentVatMode === 'incl';
    const displayPrice = showInclVat ? formattedOriginalInclVat : formattedOriginal;
    
    priceContainer.innerHTML = `
      <span
        class="price-item price-item--regular"
        data-vat-excl="${formattedOriginal}${currencyCode ? ' ' + currencyCode : ''}"
        data-vat-incl="${formattedOriginalInclVat}${currencyCode ? ' ' + currencyCode : ''}"
      >
        ${displayPrice}${currencyCode ? ' ' + currencyCode : ''}
      </span>
    `;
    
    // Re-apply VAT mode to ensure consistency
    this.reapplyVatMode();
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
      // Store the new original price after updating
      this.storeOriginalPrice();
      
      // If there's an active quantity selector button with a discount, re-apply it
      const section = this.closest('.shopify-section, dialog');
      if (section) {
        const quantitySelector = section.querySelector('quantity-selector-sets');
        if (quantitySelector) {
          const activeButton = quantitySelector.querySelector('.quantity-set-btn.active');
          if (activeButton) {
            const discount = parseInt(activeButton.dataset.discount) || 0;
            if (discount > 0) {
              // Re-trigger discount update with the active button's discount
              this.updatePriceWithDiscount({
                detail: {
                  quantity: parseInt(activeButton.dataset.quantity) || 1,
                  discountPercent: discount
                }
              });
            }
          }
        }
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
